import React, { useEffect, useState, useCallback, useRef } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate, useParams } from "react-router-dom";
import { validationSchema } from "../../../service/validation/booking";
import Input from "../../../components/FormInputs";
import Header from "../../../components/Header";
import AdminContainer from "../../Admin";
import FormTitle from "../../../components/FormTitle";
import CustomTabs from "../../../components/Tab";
import DatePicker from "../../../components/Datepicker";
import CustomDropDown from "../../../components/Dropdown/CustomDropDown";
import { AUTH_URL, MASTER_URL, OPERTIONS_URL } from "../../../common/common";
import { addBookingsTab } from "../../../common/tabs";
import { ApiService } from "../../../service/api.service";
import UploadDocumentsModal from "../../Forms/Upload/Upload";
import ConsignorDetails from "../Booking/client";
import ConsigneeDetails from "../Booking/Consignee";
import Routes from "../Booking/Routes";
import Vehicles from "../Booking/Vehicles";
import CargoDetails from "../Booking/cargodetails";
import Address from "../Booking/Address";
import Container from "../Booking/Container";
import ClientDetailes from "../Booking/ClientDetailes";
import { BookingFormValues } from "../../../service/interfaces/Components";
import Modal from "../../../components/Modal/SaveDraft";
import SecureImage from "../../../components/BucketImage/Image";
import { booking, client, Upload } from "../../../common/svg";
import DocumentComponent from "../Client/Components/document";
import { useDispatch, useSelector } from "react-redux";
import {
  resetBookingForm,
  setFullBookingForm,
} from "../../../redux/container/draftFormSlice";
import toast from "react-hot-toast";
import { getDraftData, getOneDraftData } from "../../../Utility/apicall";
import DataTableModal from "../../../components/Modal/DataTableModal";
import { FormSections } from "../../../service/interfaces/props.interface";
import EmployeeModalDropDown from "../../../components/Dropdown/EmployeeModalDropDown";

const DRAFT_STORAGE_KEY = "booking_Draft";
const AUTO_SAVE_INTERVAL = 5000;

const ManageBookingDraft: React.FC = () => {
  const { id } = useParams()
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const bookingFormState = useSelector(
    (state: { bookingForm: any }) => state.bookingForm
  );
  const entityId = localStorage.getItem("entity_id");

  // UI state
  const [activeTab, setActiveTab] = useState<string>("Basic Details");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showNavigationDialog, setShowNavigationDialog] = useState(false);
  const [draftSaved, setDraftSaved] = useState(false);
  const [formIsDirty, setFormIsDirty] = useState(false);
  const [errorsList, setErrorsList] = useState<string[]>([]);
  const [options, setOptions] = useState<any[]>([]);
  const didFetchRef = useRef(false);

  // Files state
  const [files, setFiles] = useState<File[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<any[]>([]);
  const [uploadedImages, setUploadedImages] = useState<
    { url: string; name: string }[]
  >([]);

  // Form related data
  const [bookingType, setBookingType] = useState<any>([]);
  const [movementMode, setMovementMode] = useState<any>([]);
  const [indentType, setIndentType] = useState<any>([]);
  const [employeeOptions, setEmployeeOptions] = useState<any>([]);
  const [SelectedDraft, setSelectedDraft] = useState<any>(null);
  const [draftId, setDraftId] = useState<any>(id || null);
  const [empSearch, setEmpSearch] = useState<string>("");

  const [formSections, setFormSections] = useState<FormSections>({
    consignorDetails: [],
    consigneeDetails: [],
    vehicleDetails: [],
    clientDetail: {},
    routeDetails: [],
    cargoDetails: [],
    containerDetails: [],
    billingAddress: [],
    shippingAddresses: [],
  });

  const updateFormSection = useCallback((section: string, data: any) => {
    setFormSections((prev) => {
      // Compare the current data with new data to prevent unnecessary updates
      if (JSON.stringify(prev[section]) === JSON.stringify(data)) {
        return prev; // Return the same object reference if data hasn't changed
      }
      return {
        ...prev,
        [section]: data,
      };
    });
  }, []);

  const apiService = new ApiService();

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    trigger,
    reset,
    watch,
    control,
    setValue,
    getValues,
  } = useForm<BookingFormValues>({
    resolver: yupResolver(validationSchema) as any,
    defaultValues: bookingFormState || {},
  });

  // Monitor form dirty state
  const formValues = watch();
  useEffect(() => {
    // This will run whenever any form value changes
    if (isDirty) {
      setFormIsDirty(true);
      // console.log("Form is now dirty, will auto-save soon");
    }
  }, [isDirty, formValues]);

  const saveDraft = useCallback(() => {
    // console.log("Running saveDraft function");
    const formValues = getValues();
    // console.log(formValues, "values");
    // Combine form values with section data
    const draftData = {
      ...formValues,
      ...formSections,
      clientDetail: {
        clientId: formValues?.clientDetail?.clientId || "", // ⬅️ default to empty string
        executive: formValues?.clientDetail?.executive || "",
        contactNumber: formValues?.clientDetail?.contactNumber || "",
        emailId: formValues?.clientDetail?.emailId || "",
      },
      uploadedFiles,
      uploadedImages,
    };

    // Save to localStorage and Redux
    try {
      // console.log(draftData, "draft");
      localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(draftData));
      dispatch(setFullBookingForm(draftData));

      setDraftSaved(true);
      setFormIsDirty(false); // Reset the dirty state after saving
      // console.log("Draft saved successfully");
      return draftData;
    } catch (error) {
      console.error("Error saving draft:", error);
      // Don't reset formIsDirty so it will try again
      return null;
    }
  }, [getValues, formSections, uploadedFiles, uploadedImages, dispatch]);

  // Auto-save draft at intervals
  useEffect(() => {
    // console.log("Setting up auto-save interval");

    const interval = setInterval(() => {
      if (formIsDirty) {
        // console.log("Auto-saving draft...");
        try {
          saveDraft();
          // console.log("Auto-save completed successfully");
        } catch (error) {
          console.error("Auto-save failed:", error);
        }
      }
    }, AUTO_SAVE_INTERVAL);

    return () => {
      // console.log("Cleaning up auto-save interval");
      clearInterval(interval);
    };
  }, [formIsDirty, saveDraft]);

  // Initialize booking number with timestamp format
  useEffect(() => {
    const generateBookingNumber = () => {
      const date = new Date();
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = String(date.getFullYear()).slice(-2);
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      const seconds = String(date.getSeconds()).padStart(2, "0");

      return `${day}${month}${year}${hours}${minutes}${seconds}`;
    };

    setValue("bookingNumber", generateBookingNumber());
  }, [setValue]);

  useEffect(() => {
    const fetchData = async () => {
      if (!draftId) return;

      try {
        const response = await getOneDraftData(draftId);
        if (!response || !Array.isArray(response) || response.length === 0) {
          console.error("Invalid response from API:", response);
          // toast.error("Could not load draft: Invalid data format");
          return;
        }

        // setDraftId(SelectedDraft.bookingId);

        const draftData = response[0]?.bookingdata || {};
        const { bookingDetails = {}, clientDetails = {} } = draftData;

        // Set booking detail fields
        Object.entries(bookingDetails).forEach(([key, value]) => {
          if (value !== null && value !== undefined) {
            const typedValue = value as
              | string
              | {
                companyName: string;
                executive: string;
                contactNumber: string;
                emailId: string;
              }[]
              | {
                clientId: string;
                executive: string;
                contactNumber: string;
                emailId: string;
              }
              | undefined;
            setValue(key as keyof BookingFormValues, typedValue);
          }
        });

        // Set client detail
        setValue("clientDetail", clientDetails);
        setValue("consignorDetails", draftData.consignors || []);

        // Dynamically map other sections
        const dynamicSections = {
          consignorDetails: draftData.consignors || [],
          consigneeDetails: draftData.consignees || [],
          vehicleDetails: draftData.vehicles || [],
          routeDetails: draftData.routes || [],
          cargoDetails: draftData.cargoDetails || [],
          containerDetails: draftData.containers || [],
          billingAddress: draftData.billingAddress || {},
          shippingAddresses: draftData.shippingAddress || [],
          clientDetail: draftData.clientDetails || {},
        };

        setFormSections(dynamicSections);

        // Handle booking files
        if (Array.isArray(draftData.bookingFiles)) {
          setUploadedFiles(draftData.bookingFiles);
        }

        // Final flags and toast
        setFormIsDirty(false);
        setDraftSaved(true);
        setModalOpen(false);
        toast.success("Draft loaded successfully");
      } catch (error) {
        console.error("Error loading draft:", error);
        toast.error("Failed to load draft");
      }
    };

    fetchData();
  }, [draftId]);

  const getMasterData = async () => {
    const response: any = await apiService.getApi(
      `${MASTER_URL}/booking/data-route?key=Booking Type`
    );
    if (response.status === "200") {
      if (response.body["Booking Type"]) {
        getEmployeeData(); // Uncommented to load employee data on form initialization
        const formattedOptions = response.body["Booking Type"].map(
          (item: any) => ({
            value: item.metaId,
            label: item.value,
          })
        );
        setBookingType(formattedOptions);
      }
      if (response.body["Indent Type"]) {
        const formattedOptions = response.body["Indent Type"].map(
          (item: any) => ({
            value: item.metaId,
            label: item.value,
          })
        );
        setIndentType(formattedOptions);
      }
      if (response.body["Movement Mode"]) {
        const formattedOptions = response.body["Movement Mode"].map(
          (item: any) => ({
            value: item.metaId,
            label: item.value,
          })
        );
        setMovementMode(formattedOptions);
      }
    }
  };

  const getEmployeeData = async () => {
    // console.log("entityId:", entityId);
    if (!entityId) return; // prevent invalid API calls

    const response: any = await apiService.getApi(
      `${AUTH_URL}/employee/get-route?entityId=${entityId}&search=${empSearch}&status=1`
    );

    if (response.status === "200" && response.body) {
      const formattedOptions = response.body?.data?.map((item: any) => ({
        value: item.employeeid,
        label: item.firstname + " " + item.lastname,
      }));
      setEmployeeOptions(formattedOptions);
    }
  };

  useEffect(() => {
    if (!entityId || didFetchRef.current) return;
    getMasterData();
    didFetchRef.current = true;
    return () => {
      didFetchRef.current = false;
    };
  }, [entityId]);

  // Load saved draft on component mount
  useEffect(() => {
    const loadSavedDraft = () => {
      const draftData = localStorage.getItem(DRAFT_STORAGE_KEY);
      if (!draftData) return;

      try {
        const parsedDraft = JSON.parse(draftData);

        // Reset form with all available values
        reset(parsedDraft);
        // console.log("Loaded Draft Data:", parsedDraft);
        // Update form sections with draft data
        setFormSections({
          consignorDetails: parsedDraft.consignorDetails || [],
          consigneeDetails: parsedDraft.consigneeDetails || [],
          vehicleDetails: parsedDraft.vehicleDetails || [],
          clientDetail: parsedDraft.clientDetail || {},
          routeDetails: parsedDraft.routeDetails || [],
          cargoDetails: parsedDraft.cargoDetails || [],
          containerDetails: parsedDraft.containerDetails || [],
          billingAddress: parsedDraft.billingAddress || [],
          shippingAddresses: parsedDraft.shippingAddresses || [],
        });

        // Restore file data if it exists
        if (parsedDraft.uploadedFiles) {
          setUploadedFiles(parsedDraft.uploadedFiles);
        }
        if (parsedDraft.uploadedImages) {
          setUploadedImages(parsedDraft.uploadedImages);
        }

        setDraftSaved(true);
      } catch (error) {
        console.error("Error loading draft:", error);
      }
    };

    loadSavedDraft();
  }, [reset]);

  // Update handleSaveDraft accordingly
  const handleSaveDraft = async () => {
    let data = formValues;
    if (data.bookingName && data.bookingNumber) {
      try {
        const formData = new FormData();

        // Add entityId
        if (entityId) {
          formData.append("entityId", entityId);
        }

        // Helper function to filter and append object data
        const appendObjectData = (key: string, data: any) => {
          if (!data) return;

          // Convert to array if not already
          const dataArray = Array.isArray(data) ? data : [data];

          // Remove empty values and filter out empty objects
          const filteredData = dataArray
            .map((item) =>
              Object.fromEntries(
                Object.entries(item).filter(
                  ([_, value]) =>
                    value !== null && value !== undefined && value !== ""
                )
              )
            )
            .filter((item) => Object.keys(item).length > 0);

          if (filteredData.length > 0) {
            formData.append(
              key,
              JSON.stringify(
                Array.isArray(data) ? filteredData : filteredData[0]
              )
            );
          }
        };

        // Booking details
        const bookingDetail = {
          ...(draftId ? { bookingId: draftId } : {}),
          bookingName: data.bookingName,
          bookingNumber: data.bookingNumber,
          bookingType: data.bookingType,
          startDate: data.startDate,
          movementMode: data.movementMode,
          bookingCreatedBy: data.bookingCreatedBy,
          indentType: data.indentType,
          endDate: data.endDate,
          status: "677a3fe9-00cf-4f71-a1f1-3bf21bce6443",
        };

        appendObjectData("bookingDetail", bookingDetail);

        // Append all section data
        appendObjectData("consignorDetails", formSections.consignorDetails);
        appendObjectData("consigneeDetails", formSections.consigneeDetails);
        appendObjectData("clientDetail", formSections.clientDetail);
        appendObjectData("cargoDetails", formSections.cargoDetails);
        appendObjectData("routeDetails", formSections.routeDetails);
        appendObjectData("vehicleDetails", formSections.vehicleDetails);
        appendObjectData("containerDetails", formSections.containerDetails);
        appendObjectData("billingAddress", formSections.billingAddress);
        appendObjectData("shippingAddresses", formSections.shippingAddresses);

        // Existing Documents
        const existingDocuments = uploadedFiles
          ?.filter((file) => file.fileId)
          .map((file) => ({
            fileId: file.fileId,
            fileName: file.fileName,
            s3BucketName: file.s3BucketName,
            s3ObjectKey: file.s3ObjectKey,
          }));

        if (existingDocuments && existingDocuments.length > 0) {
          formData.append("contactFiles", JSON.stringify(existingDocuments));
        }

        // Add new files
        if (files?.length > 0) {
          files.forEach((file) => {
            formData.append("files", file);
          });
        }

        // Reset form state

        // console.log(formData.get("bookingDetail"), "form");
        // Submit form
        const response = draftId
          ? await apiService.putApi(
            `${OPERTIONS_URL}/draft-booking/update-route`,
            // `http://192.168.0.104:6048/api/operational/draft-booking/store-route`,
            formData
          )
          : await apiService.postApi(
            `${OPERTIONS_URL}/draft-booking/store-route`,
            // `http://192.168.0.104:6048/api/operational/draft-booking/store-route`,
            formData
          );

        if (response && response.status?.toString() === "200") {
          setFormIsDirty(false);
          localStorage.removeItem(DRAFT_STORAGE_KEY);
          dispatch(resetBookingForm());
          navigate("/booking");
        } else {
          console.error("Error in response:", response);
        }
      } catch (error: any) {
        if (error.name === "CanceledError" || error.name === "AbortError") {
          console.log("Request was canceled");
          return;
        }
        console.error("Form submission error:", error);
      }
    } else {
      toast.error("booking name and number are required");
    }
  };
  const handleDiscard = () => {
    setFormIsDirty(false);
    setDraftSaved(false);
    setShowNavigationDialog(false);

    // Clear saved draft
    localStorage.removeItem(DRAFT_STORAGE_KEY);
    dispatch(resetBookingForm());

    // Navigate away
    navigate("/booking");
  };

  // Form submission
  const onSubmit = async (data: any) => {
    try {
      console.log("Form submitted:", data);
      const formData = new FormData();

      // Add entityId
      if (entityId) {
        formData.append("entityId", entityId);
      }

      // Helper function to filter and append object data
      const appendObjectData = (key: string, data: any) => {
        if (!data) return;

        // Convert to array if not already
        const dataArray = Array.isArray(data) ? data : [data];

        // Remove empty values and filter out empty objects
        const filteredData = dataArray
          .map((item) =>
            Object.fromEntries(
              Object.entries(item).filter(
                ([_, value]) =>
                  value !== null && value !== undefined && value !== ""
              )
            )
          )
          .filter((item) => Object.keys(item).length > 0);

        if (filteredData.length > 0) {
          formData.append(
            key,
            JSON.stringify(Array.isArray(data) ? filteredData : filteredData[0])
          );
        }
      };

      // Booking details
      const bookingDetail = {
        bookingName: data.bookingName,
        bookingNumber: data.bookingNumber,
        bookingType: data.bookingType,
        startDate: data.startDate,
        movementMode: data.movementMode,
        bookingCreatedBy: data.bookingCreatedBy,
        indentType: data.indentType,
        endDate: data.endDate,
        status: "677a3fe9-00cf-4f71-a1f1-3bf21bce6443",
      };

      appendObjectData("bookingDetail", bookingDetail);

      // Append all section data
      appendObjectData("consignorDetails", formSections.consignorDetails);
      appendObjectData("consigneeDetails", formSections.consigneeDetails);
      // appendObjectData("clientDetail", formSections.clientDetail);
      appendObjectData("cargoDetails", formSections.cargoDetails);
      appendObjectData("routeDetails", formSections.routeDetails);
      appendObjectData("vehicleDetails", formSections.vehicleDetails);
      appendObjectData("containerDetails", formSections.containerDetails);
      appendObjectData("billingAddress", formSections.billingAddress);
      appendObjectData("shippingAddresses", formSections.shippingAddresses);

      // Add client data
      appendObjectData("clientDetail", data.clientDetail || formSections.clientDetail);

      // Add routes data
      appendObjectData("routes", data.routes || formSections.routes);

      // Add existing documents
      const existingDocuments = uploadedFiles
        ?.filter((file) => file.fileId)
        .map((file) => ({
          fileId: file.fileId,
          fileName: file.fileName,
          s3BucketName: file.s3BucketName,
          s3ObjectKey: file.s3ObjectKey,
        }));

      if (existingDocuments?.length > 0) {
        formData.append("bookingFiles", JSON.stringify(existingDocuments));
      }

      // Add new files
      if (files?.length > 0) {
        files.forEach((file) => {
          formData.append("files", file);
        });
      }
      console.log(formData.get("clientDetail"), "form");

      // Submit form
      const response = await apiService.postApi(
        `${OPERTIONS_URL}/booking/store-route`,
        formData
      );

      if (response && response.status?.toString() === "200") {
        // Reset form state
        setFormIsDirty(false);
        localStorage.removeItem(DRAFT_STORAGE_KEY);
        dispatch(resetBookingForm());
        navigate("/booking");
      } else {
        console.error("Error in response:", response);
      }
    } catch (error: any) {
      if (error.name === "CanceledError" || error.name === "AbortError") {
        console.log("Request was canceled");
        return;
      }
      console.error("Form submission error:", error);
    }
  };

  // Toggle upload modal
  const toggleModal = () => {
    setIsModalOpen((prev) => !prev);
  };
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <div>
      <div className="mb--3">
        <Header icon={booking} title="Add New Booking" showSearch={false}>

          <div onClick={toggleModal} className="cursor-pointer">
            <SecureImage fileKey={Upload} alt="header icon" width="30px" />
          </div>
        </Header>
        {isModalOpen && (
          <UploadDocumentsModal
            uploadedImages={uploadedImages}
            setUploadedImages={setUploadedImages}
            files={files}
            setFiles={setFiles}
            toggleModal={toggleModal}
          />
        )}

        {showNavigationDialog && (
          <Modal
            title="Action"
            onContinue={() => setShowNavigationDialog(false)}
            onDiscard={handleDiscard}
            onSaveDraft={handleSaveDraft}
            message="Acction Required"
          />
        )}
      </div>

      <AdminContainer>
        <DataTableModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          fetchDataFn={getDraftData}
          onSelect={(item) => setSelectedDraft(item)}
          title="Draft Bookings"
        />
        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Booking Details */}
          <div className="bg-white p-6 rounded-[20px] font-poppins">
            <FormTitle title="Booking Details" />
            <div className="mt-4 grid grid-cols-4 gap-x-[1.5rem]">
              <Input
                label="Booking Name"
                name="bookingName"
                errors={errors}
                register={register}
                placeholder="Enter booking name"
                type="text"
                onBlur={() => trigger("bookingName")}
              />
              <Input
                label="Booking Number"
                name="bookingNumber"
                errors={errors}
                register={register}
                placeholder="Enter Booking Number"
                type="number"
                onBlur={() => trigger("bookingNumber")}
              />
              <CustomDropDown
                errors={errors}
                register={register}
                label="Booking Type"
                name="bookingType"
                options={bookingType}
                defaultValue={watch("bookingType")}
              />
              <DatePicker
                label="Starting Date & Time"
                onDateChange={(date: string) => setValue("startDate", date)}
                register={register("startDate")}
                name="startDate"
                includeTime={true}
                errors={errors}
                disabled={false}
                defaultValue={watch("startDate")}
              />
            </div>
            <div className="mt-4 grid grid-cols-4 gap-x-[1.5rem]">
              <CustomDropDown
                errors={errors}
                register={register}
                label="Movement Mode"
                name="movementMode"
                options={movementMode}
                defaultValue={watch("movementMode")}
              />
              {/* <CustomDropDown
                errors={errors}
                register={register}
                label="Booking Created By"
                name="bookingCreatedBy"
                options={employeeOptions}
                defaultValue={watch("bookingCreatedBy")}
              /> */}
              <EmployeeModalDropDown
                label="Employee"
                name="bookingCreatedBy"
                endpoint="employee/get-route"
                register={register}
                errors={errors}
                setValue={setValue}
                defaultValue={watch("bookingCreatedBy")}
              // fetchDepartments={fetchDepartments}
              />
              <CustomDropDown
                errors={errors}
                register={register}
                label="Indent Type"
                name="indentType"
                options={indentType}
                defaultValue={watch("indentType")}
              />
              <DatePicker
                label="Ending Date & Time"
                onDateChange={(date: string) => setValue("endDate", date)}
                register={register("endDate")}
                minDate={watch("startDate")}
                name="endDate"
                errors={errors}
                includeTime={true}
                disabled={false}
                defaultValue={watch("endDate")}
              />
            </div>
          </div>
          <br />

          {/* Client Details */}
          <ClientDetailes
            errors={errors}
            register={register}
            setValue={setValue}
            control={control}
            setclientDetailData={(data) =>
              updateFormSection("clientDetail", data)
            }
            clientDetail={formSections.clientDetail}
          />
          <br />

          {/* Booking Tabs */}
          <CustomTabs tabs={addBookingsTab} onTabChange={setActiveTab} />
          <br />

          {/* Tab Content */}
          {activeTab === "Basic Details" && (
            <div>
              <ConsignorDetails
                errors={errors}
                register={register}
                setValue={setValue}
                control={control}
                setconsignorDetailsData={(data) =>
                  updateFormSection("consignorDetails", data)
                }
              />
              <br />
              <ConsigneeDetails
                setconsigneeDetailsData={(data) =>
                  updateFormSection("consigneeDetails", data)
                }
                errors={errors}
                register={register}
                setValue={setValue}
                control={control}
              />
            </div>
          )}

          {activeTab === "Routes" && (
            <Routes
              errors={errors}
              register={register}
              setValue={setValue}
              control={control}
              setRouteDetailsData={(data) =>
                updateFormSection("routeDetails", data)
              }
            />
          )}

          {activeTab === "Vehicles" && (
            <Vehicles
              errors={errors}
              register={register}
              setValue={setValue}
              vehicleDetailsData={formSections.vehicleDetails}
              setvehicleDetailsData={(data) =>
                updateFormSection("vehicleDetails", data)
              }
              control={control}
            />
          )}

          {activeTab === "Cargo Details" && (
            <CargoDetails
              setCargoDetailsData={(data) =>
                updateFormSection("cargoDetails", data)
              }
              errors={errors}
              register={register}
              setValue={setValue}
              control={control}
              CargoDetailsData={formSections.cargoDetails}
              getValues={getValues}
            />
          )}

          {activeTab === "Container" && (
            <Container
              errors={errors}
              register={register}
              setValue={setValue}
              ContainerDetailsData={formSections.containerDetails}
              setContainerDetailsData={(data) =>
                updateFormSection("containerDetails", data)
              }
              control={control}
            />
          )}

          {activeTab === "Address" && (
            <Address
              setBillingDetailData={(data) =>
                updateFormSection("billingAddress", data)
              }
              setShippingDetailsData={(data) =>
                updateFormSection("shippingAddresses", data)
              }
              billingDetailData={formSections.billingAddress}
              register={register}
              errors={errors}
              setValue={setValue}
              clientData={formSections.clientDetail}
              control={control}
            />
          )}

          {activeTab === "Documents" && (
            <DocumentComponent
              uploadedFiles={uploadedFiles}
              setUploadedFiles={setUploadedFiles}
              setUploadedImages={setUploadedImages}
              uploadedImages={uploadedImages}
              setFiles={setFiles}
              files={files}
            />
          )}
          <br />

          {/* Error messages */}
          {errorsList.length > 0 && (
            <div className="flex items-center justify-between bg-red-50 border-l-4 border-red-500 p-4 rounded-lg shadow-md mb-6">
              <div className="flex items-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-8 w-8 text-red-500 mr-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
                <div>
                  <h4 className="text-lg font-semibold text-red-700 mb-2">
                    Required Fields
                  </h4>
                  <ul className="list-disc list-inside text-red-600 space-y-1">
                    {errorsList.map((error, index) => (
                      <li key={index} className="text-sm">
                        {error}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Form buttons */}
          <div className="gap-4 flex items-center justify-end mr-8 self-end">
            <button
              type="button"
              className="w-36 h-10 rounded-lg text-[#444FBD] border border-[#444FBD] hover:border-gray-400 transition duration-200"
              onClick={() => {
                if (formIsDirty) {
                  setShowNavigationDialog(true);
                } else {
                  navigate("/booking");
                }
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="w-36 h-10 rounded-lg text-[#444FBD] border border-[#444FBD] hover:border-gray-400 transition duration-200"
              onClick={handleSaveDraft}
            >
              {id && "Update Draft"}
            </button>
            <button
              type="submit"
              className="w-36 h-10 rounded-lg bg-[#444FBD] text-white transition duration-200"
              // onClick={() => {
              //   const errorMessages: string[] = [];
              //   Object.entries(errors).forEach(([key, value]) => {
              //     if (value) {
              //       errorMessages.push(`${key} is required`);
              //       toast.error(`${key} is required`);
              //     }
              //   });
              // }}
              onClick={() => {
                const hasErrors = Object.values(errors).some((value) => value);
                if (hasErrors) {
                  toast.error("Please fill all the required fields");
                }
              }}

              disabled={errorsList.length > 0}
            >
              Submit
            </button>
          </div>
        </form>
      </AdminContainer>
    </div>
  );
};

export default ManageBookingDraft;
